import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';

import { Environment } from '@/react/portainer/environments/types';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { withGlobalError } from '@/react-tools/react-query';

import { Registry } from '../../types/registry';
import { buildUrl } from '../../queries/build-url';

import { queryKeys } from './queryKeys';

export function useRepositoryTags({
  registryId,
  enabled = true,
  ...params
}: {
  registryId: Registry['Id'];
  repository: string;
  environmentId?: Environment['Id'];
  enabled?: boolean;
}) {
  return useQuery(
    queryKeys.repositoryTags(registryId, params.repository),
    () =>
      getRepositoryTags({
        ...params,
        registryId,
        n: 100,
        last: '',
      }),
    {
      enabled,
      staleTime: 1 * 60 * 1000, // 1 minute
      ...withGlobalError(),
    }
  );
}

export async function getRepositoryTags(
  {
    environmentId,
    registryId,
    repository,
    n,
    last,
  }: {
    registryId: Registry['Id'];
    repository: string;
    environmentId?: Environment['Id'];
    n?: number;
    last?: string;
  },
  acc: { name: string; tags: string[] } = { name: '', tags: [] }
): Promise<{ name: string; tags: string[] }> {
  try {
    const { data, headers } = await axios.get<{ name: string; tags: string[] }>(
      `${buildUrl(registryId)}/v2/${repository}/tags/list`,
      {
        params: {
          endpointId: environmentId,
          n,
          last,
        },

        // The 'X-RegistryManagement-ForceNew' header forces the backend to create a new registry client instance for this request.
        // This is required to avoid issues with stale or cached registry connections, ensuring the tag list is up-to-date.
        headers: { 'X-RegistryManagement-ForceNew': '1' },
      }
    );
    acc.name = data.name;
    acc.tags = _.concat(acc.tags, data.tags ?? []);

    if (headers.link) {
      const last = data.tags[data.tags.length - 1];
      return await getRepositoryTags(
        { registryId, repository, n, last, environmentId },
        acc
      );
    }

    return acc;
  } catch (err) {
    throw parseAxiosError(err);
  }
}
